/**
 * StaffGegevensView — Read-only staff details
 * Shows all personal, employment, salary info in a clean layout.
 */

import { useState } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { Staff } from '../../stores/workforce-store';
import { supabase } from '../../lib/supabase';

interface StaffGegevensViewProps {
  staff: Staff;
  onEdit: () => void;
}

const contractLabels: Record<string, string> = {
  vast: 'Vast',
  jaaruren: 'Jaaruren',
  'min-max': 'Min-max',
  '0-uren': '0-uren',
  student: 'Student',
  zzp: 'ZZP',
  dga: 'DGA',
};

function formatDate(d: string | null): string {
  if (!d) return '—';
  return format(new Date(d + 'T00:00:00'), 'd MMMM yyyy', { locale: nl });
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between items-center py-2.5 px-4">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm ${value && value !== '—' ? 'font-medium text-slate-900' : 'text-slate-400'}`}>
        {value || '—'}
      </span>
    </div>
  );
}

export function StaffGegevensView({ staff, onEdit }: StaffGegevensViewProps) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownloadIntakePdf() {
    setDownloading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Niet ingelogd');
      const res = await fetch(`/api/workforce/salaris-intake-pdf?staff_id=${staff.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) throw new Error('PDF download mislukt');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = staff.name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      a.download = `intake-salarisadministratie-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert((e as Error).message || 'PDF download mislukt');
    } finally {
      setDownloading(false);
    }
  }

  const werkroosterSummary = staff.werkrooster?.length
    ? staff.werkrooster
        .sort((a, b) => a.day - b.day)
        .map(w => {
          const days = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
          return `${days[w.day]} ${w.from}–${w.to}`;
        })
        .join(', ')
    : null;

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">Gegevens</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadIntakePdf}
            disabled={downloading}
            className="px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
            title="Download een PDF met alle gegevens voor de salarisadministratie"
          >
            {downloading ? 'PDF maken…' : 'PDF voor salarisadministratie'}
          </button>
          <button
            onClick={onEdit}
            className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            Bewerken
          </button>
        </div>
      </div>

      {/* Persoonlijk */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Persoonlijk</span>
        </div>
        <div className="divide-y divide-slate-50">
          <Row label="Naam" value={staff.name} />
          <Row label="Aanhef" value={staff.aanhef} />
          <Row label="Voorletters" value={staff.voorletters} />
          <Row label="Geboortedatum" value={formatDate(staff.geboortedatum)} />
          <Row label="Geboorteplaats" value={(staff as Record<string, unknown>).geboorteplaats as string} />
          <Row label="Nationaliteit" value={(staff as Record<string, unknown>).nationaliteit as string} />
          <Row label="BSN" value={(staff as Record<string, unknown>).bsn as string} />
        </div>
      </div>

      {/* Adres & Contact */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Adres & Contact</span>
        </div>
        <div className="divide-y divide-slate-50">
          <Row label="Adres" value={(staff as Record<string, unknown>).adres as string} />
          <Row label="Postcode" value={(staff as Record<string, unknown>).postcode as string} />
          <Row label="Woonplaats" value={staff.woonplaats || (staff as Record<string, unknown>).stad as string} />
          <Row label="Email" value={staff.email} />
          <Row label="Telefoon" value={staff.phone} />
          <Row label="Noodnummer" value={(staff as Record<string, unknown>).noodnummer as string} />
          <Row label="Noodcontact" value={(staff as Record<string, unknown>).noodcontact_naam as string} />
          <Row label="Bankrekening" value={(staff as Record<string, unknown>).bankrekening as string} />
        </div>
      </div>

      {/* Dienstverband */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Dienstverband</span>
        </div>
        <div className="divide-y divide-slate-50">
          <Row label="Functie" value={(staff as Record<string, unknown>).functie as string} />
          <Row label="Team" value={staff.team ? staff.team.charAt(0).toUpperCase() + staff.team.slice(1) : null} />
          <Row label="Contract type" value={contractLabels[staff.contract_type]} />
          <Row label="Datum in dienst" value={formatDate(staff.start_date)} />
          {staff.end_date && <Row label="Einddatum" value={formatDate(staff.end_date)} />}
          <Row label="Uren per week" value={staff.uren_per_week ? `${staff.uren_per_week}u` : null} />
          <Row label="FTE" value={staff.fte_percentage ? `${staff.fte_percentage}%` : null} />
        </div>
      </div>

      {/* Salaris */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Salaris</span>
        </div>
        <div className="divide-y divide-slate-50">
          <Row label="Loongroep (CAO)" value={staff.loongroep ? `Loongroep ${staff.loongroep}` : null} />
          <Row label="Uurloon" value={staff.uurloon ? `\u20AC ${staff.uurloon.toFixed(2)}` : null} />
          <Row label="Loontype" value={staff.loon_type === 'all-in' ? 'All-in' : 'Bruto'} />
          <Row label="Reiskostenvergoeding/dag" value={staff.reiskostenvergoeding_per_dag ? `\u20AC ${staff.reiskostenvergoeding_per_dag.toFixed(2)}` : null} />
          {staff.uurloon_handmatig && <Row label="Uurloon instelling" value="Handmatig (geen CAO sync)" />}
        </div>
      </div>

      {/* Werkrooster */}
      {werkroosterSummary && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Werkrooster</span>
          </div>
          <div className="px-4 py-3 text-sm text-slate-700">{werkroosterSummary}</div>
        </div>
      )}

      {/* Systeem */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Systeem</span>
        </div>
        <div className="divide-y divide-slate-50">
          <Row label="PIN" value={staff.pin_code ? `${staff.pin_code}` : null} />
          <Row label="Salesforce" value={staff.sf_medewerker_id ? `Gekoppeld (${staff.sf_medewerker_id})` : 'Niet gekoppeld'} />
        </div>
      </div>
    </div>
  );
}
